import { supabase } from '../lib/supabase';

// ─── Case conversion helpers ───────────────────────────────────────────────

export const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    // JSONB entitlement keys are runtime identifiers (for example
    // `white_label`), not database column names. Preserve them exactly.
    acc[camelKey] = key === 'feature_entitlements'
      ? obj?.[key]
      : typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

export const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    // Keep entitlement runtime keys stable on writes for the same reason as
    // the read-side exception above.
    acc[snakeKey] = key === 'featureEntitlements'
      ? obj?.[key]
      : typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

export const toPricingTierPayload = (tierData) => {
  const persistedFields = { ...(tierData || {}) };
  delete persistedFields.customPlanKey;
  return toSnakeCase(persistedFields);
};

// ─── Admin status checks ───────────────────────────────────────────────────

export const isAdmin = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return false;
    const { data, error } = await supabase?.from('admin_users')?.select('id, is_active')?.eq('id', user?.id)?.eq('is_active', true)?.maybeSingle();
    if (error) return false;
    return data != null;
  } catch {
    return false;
  }
};

export const isOwner = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return false;
    const { data, error } = await supabase?.from('admin_users')?.select('role, is_active')?.eq('id', user?.id)?.eq('is_active', true)?.maybeSingle();
    if (error || !data) return false;
    return data?.role === 'owner';
  } catch {
    return false;
  }
};

// ─── Admin profile ─────────────────────────────────────────────────────────

export const getAdminProfile = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase?.from('admin_users')?.select('*')?.eq('id', user?.id)?.single();
    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('getAdminProfile error:', error);
    throw error;
  }
};

// ─── System statistics ─────────────────────────────────────────────────────

export const getSystemStats = async () => {
  try {
    const [usersResult, formsResult, submissionsResult] = await Promise.all([
      supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true }),
      supabase?.from('forms')?.select('id', { count: 'exact', head: true }),
      supabase?.from('form_submissions')?.select('id', { count: 'exact', head: true }),
    ]);
    return {
      totalAccounts: usersResult?.count || 0,
      totalForms: formsResult?.count || 0,
      totalSubmissions: submissionsResult?.count || 0,
      activeUsers: usersResult?.count || 0,
    };
  } catch (error) {
    console.error('getSystemStats error:', error);
    throw error;
  }
};

// ─── Account management ────────────────────────────────────────────────────

export const getAllAccounts = async () => {
  try {
    // Fetch all user profiles
    const { data: profiles, error: profilesError } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    if (profilesError) throw profilesError;

    if (!profiles || profiles?.length === 0) return [];

    // Fetch live form counts per user from the forms table
    const { data: formCounts, error: formsError } = await supabase
      ?.from('forms')
      ?.select('user_id, id, submissions_count');

    if (formsError) {
      console.warn('Could not fetch form counts:', formsError);
    }

    // Build a map: userId -> { totalForms, totalSubmissions }
    const formStatsMap = {};
    if (formCounts) {
      for (const form of formCounts) {
        if (!form?.user_id) continue;
        if (!formStatsMap?.[form?.user_id]) {
          formStatsMap[form.user_id] = { totalForms: 0, totalSubmissions: 0 };
        }
        formStatsMap[form.user_id].totalForms += 1;
        formStatsMap[form.user_id].totalSubmissions += (form?.submissions_count || 0);
      }
    }

    // Merge live stats into each profile
    const enriched = profiles?.map((profile) => {
      const stats = formStatsMap?.[profile?.id] || { totalForms: 0, totalSubmissions: 0 };
      return {
        ...profile,
        total_forms: stats?.totalForms,
        total_submissions: stats?.totalSubmissions,
      };
    });

    return toCamelCase(enriched);
  } catch (error) {
    console.error('getAllAccounts error:', error);
    throw error;
  }
};

export const getAccountById = async (accountId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', accountId)?.single();
    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('getAccountById error:', error);
    throw error;
  }
};

// Update account via the admin-update-account Edge Function
// (service-role key, bypasses RLS for admin writes).
export const updateAccount = async (accountId, updates) => {
  // Only send fields the caller explicitly supplied — omitted fields must be
  // left out of the body entirely, otherwise the Edge Function would treat
  // them (e.g. companyName: null) as an explicit request to clear the value.
  const body = { userId: accountId };
  if (updates?.planType !== undefined && updates?.planType !== '') {
    body.planType = updates.planType;
  }
  if (updates?.subscriptionStatus !== undefined && updates?.subscriptionStatus !== '') {
    body.subscriptionStatus = updates.subscriptionStatus;
  }
  if (updates?.companyName !== undefined) {
    // Explicit empty/whitespace means "clear the company name".
    body.companyName = updates.companyName?.trim() || null;
  }
  if (updates?.featureFlags !== undefined) {
    body.featureFlags = updates.featureFlags;
  }

  const { data, error } = await supabase?.functions?.invoke('admin-update-account', {
    body,
  });

  if (error) {
    console.error('admin-update-account edge function error:', error);
    const isUnreachable =
      error?.message?.toLowerCase()?.includes('not found') ||
      error?.message?.toLowerCase()?.includes('relay') ||
      error?.context?.status === 404;
    if (isUnreachable) {
      throw new Error('The account update service is unreachable. Please try again or contact support if the problem persists.');
    }
    throw new Error(data?.error || error?.message || 'Failed to update account');
  }

  if (data?.error) {
    console.error('admin-update-account body error:', data.error);
    throw new Error(data.error);
  }

  return data?.profile ? toCamelCase(data.profile) : null;
};

// Get forms for a specific account (admin only)
export const getAccountForms = async (accountId) => {
  try {
    const { data, error } = await supabase?.from('forms')?.select('id, name, title, status, published, submissions_count, created_at, updated_at')?.eq('user_id', accountId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])?.map((form) => ({
      id: form?.id,
      name: form?.name || form?.title || 'Untitled Form',
      title: form?.title || form?.name || 'Untitled Form',
      status: form?.status,
      published: form?.published,
      submissionsCount: form?.submissions_count || 0,
      createdAt: form?.created_at,
      updatedAt: form?.updated_at,
    }));
  } catch (error) {
    console.error('getAccountForms error:', error);
    return [];
  }
};

// Get submissions for a specific account (admin only)
export const getAccountSubmissions = async (accountId) => {
  try {
    const { data: forms, error: formsError } = await supabase?.from('forms')?.select('id')?.eq('user_id', accountId);
    if (formsError) throw formsError;
    if (!forms || forms?.length === 0) return [];

    const formIds = forms?.map((f) => f?.id);
    const { data, error } = await supabase?.from('form_submissions')?.select('id, form_id, created_at, status, user_email')?.in('form_id', formIds)?.order('created_at', { ascending: false });
    if (error) throw error;
    return toCamelCase(data || []);
  } catch (error) {
    console.error('getAccountSubmissions error:', error);
    return [];
  }
};

// ─── Pricing tiers ─────────────────────────────────────────────────────────

export const getAllPricingTiers = async () => {
  try {
    const { data, error } = await supabase?.from('pricing_tiers')?.select('*')?.eq('is_active', true)?.order('sort_order', { ascending: true });
    if (error) throw error;
    return toCamelCase(data || []);
  } catch (error) {
    console.error('getAllPricingTiers error:', error);
    throw error;
  }
};

export const getAllPricingTiersAdmin = async () => {
  try {
    const { data, error } = await supabase?.from('pricing_tiers')?.select('*')?.order('sort_order', { ascending: true });
    if (error) throw error;
    return toCamelCase(data || []);
  } catch (error) {
    console.error('getAllPricingTiersAdmin error:', error);
    throw error;
  }
};

export const createPricingTier = async (tierData) => {
  try {
    const { data, error } = await supabase?.from('pricing_tiers')?.insert([toPricingTierPayload(tierData)])?.select()?.single();
    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('createPricingTier error:', error);
    throw error;
  }
};

export const updatePricingTier = async (tierId, updates) => {
  try {
    const { data, error } = await supabase?.from('pricing_tiers')?.update({ ...toPricingTierPayload(updates), updated_at: new Date()?.toISOString() })?.eq('id', tierId)?.select()?.single();
    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('updatePricingTier error:', error);
    throw error;
  }
};

export const deletePricingTier = async (tierId) => {
  try {
    const { error } = await supabase?.from('pricing_tiers')?.delete()?.eq('id', tierId);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('deletePricingTier error:', error);
    throw error;
  }
};

// ─── Account feature flags ─────────────────────────────────────────────────

// Update per-account feature flags (white_label, client_workspaces, etc.)
// Update feature flags via the admin-update-account Edge Function
// (service-role key, bypasses the RLS policy that blocks anon-key writes).
export const updateAccountFeatures = async (accountId, features) => {
  try {
    return await updateAccount(accountId, { featureFlags: features });
  } catch (error) {
    console.error('updateAccountFeatures error:', error);
    throw error;
  }
};

// ─── Named export object (for components that import { adminService }) ──────

export const adminService = {
  isAdmin,
  isOwner,
  getAdminProfile,
  getSystemStats,
  getAllAccounts,
  getAccountById,
  updateAccount,
  updateAccountFeatures,
  getAccountForms,
  getAccountSubmissions,
  getAllPricingTiers,
  getAllPricingTiersAdmin,
  createPricingTier,
  updatePricingTier,
  deletePricingTier,
};